import React, {
  useContext, useState, useEffect, useMemo
} from 'react'
import { Image } from '@telus/koodo-ui-kit'
import useBrand from '@engine/hooks/useBrand'
import Brands from '@brands/index'
import { useHistory } from 'react-router-dom'
import {
  PHONE_NUMBER_STATE,
  transferANumberWirelineState,
  transferANumberWirelessState,
  updatePhoneNumberState
} from '@apps/redux/features/PhoneNumberSlice'
import ProxyOperations from '@apps/flows/activation/services/operations'
import { PLANS_STATE } from '@apps/redux/features/PlansSlice'
import { useDispatch, useSelector } from 'react-redux'
import ConsumerActivationPaths from '@sites/consumer/activation/ConsumerActivationPaths'
import { ApplicationContext, APPLICATION_STATE } from '@apps/contexts/ApplicationContext'
import PlanUnavailablePopup from '@apps/components/PlanUnavailablePopup'
import PhoneNumberV2 from '@apps/components/PhoneNumber/PhoneNumberV2'
import Sites from '@sites/index'
import { getById, getBySlug, getImgByTitle } from '@apps/utils/contentful'
import Checkbox from '../Checkbox'
import TransferLandline from './TransferLandline'
import TransferMobile from './TransferMobile'
import Heading4 from '../Typography/Heading4'
import BodyError from '../Typography/BodyError'
import Body from '../Typography/Body'
import BodySmall from '../Typography/BodySmall'
import { getTaxProvince } from '@/apps/utils/common'
import {
  PAYMENT_STATE,
  updatePaymentStates,
} from "../../redux/features/PayAndReviewSlice"

const TransferNumber = ({
  transferRefs,
  transferHandlers,
  chooseNewPhoneNumber,
  pushClickAnalytics
}) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const { getTax } = ProxyOperations('public')

  const consumerActivationPaths = new ConsumerActivationPaths()

  const { [PLANS_STATE.PRE_SELECTED_PLAN]: preSelectedPlan } = useSelector(
    (state) => state.plansPage
  )

  const phoneNumberState = useSelector((state) => state.phoneNumberPage)
  const {
    [PHONE_NUMBER_STATE.TRANSFER_A_NUMBER.IS_PORT_IN_NUMBER_ELIGIBLE]: isPortInNumberEligible,
    [PHONE_NUMBER_STATE.TRANSFER_A_NUMBER.IS_PORT_IN_AGREEMENT_CHECKED]: isPortInAgreementChecked,
    [PHONE_NUMBER_STATE.TRANSFER_A_NUMBER.IS_PORT_IN_WIRELESS]: isPortInWireless,
    [PHONE_NUMBER_STATE.TRANSFER_A_NUMBER.IS_PORT_IN_HAS_AGREEMENT_ERROR]: hasAgreementError,
    [PHONE_NUMBER_STATE.SELECTED_PHONE_NUMBER]: selectedPhoneNumber,
  } = phoneNumberState

  const simSelectionState = useSelector((state) => state.simSelectionPage)
  const { cost: simCost } = simSelectionState.selectSimType

  const {
    [PAYMENT_STATE.SUMMARY.TAX_PROVINCE]: taxProvince,
  } = useSelector((state) => state.payAndReviewPage)

  const {
    [APPLICATION_STATE.CURRENT_LANGUAGE]: language
  } = useContext(ApplicationContext)

  const [hasCheckedOnce, setHasCheckedOnce] = useState(false)

  useEffect(() => {
    if (isPortInAgreementChecked && !hasCheckedOnce) {
      setHasCheckedOnce(true)
    }
  }, [isPortInAgreementChecked])

  const {
    content: { pageContent },
    user: { selectedLanguage },
    userType
  } = useSelector((state) => state.common)

  const isDealer = userType === Sites.dealer
  const pagePrefix = isDealer ? 'Dealer' : 'Consumer'
  const textContent = useMemo(() => {
    if (!pageContent) return {}
    const commonElements = getById(pageContent, 'commonElements')[selectedLanguage]
    const phoneNumberPage = getById(pageContent, `phoneNumberPage${pagePrefix}`)[selectedLanguage]
    const { shortTextFields } = phoneNumberPage || {}

    return {
      transferOptInPublic: getBySlug(shortTextFields, 'transferOptInPublic')?.value,
      transferOptInKoodo: getBySlug(shortTextFields, 'transferOptInKoodo')?.value,
      transferOptInError: getBySlug(shortTextFields, 'transferOptInError')?.value,
      transferLandlineSubtitle: getBySlug(shortTextFields, 'transferLandlineSubtitle')?.value,
      transferLandlineFootnote: getBySlug(shortTextFields, 'transferLandlineFootnote')?.value,
      error: getImgByTitle(commonElements, 'error')?.url
    }
  }, [pageContent, selectedLanguage])

  const {
    transferOptInPublic,
    transferOptInKoodo,
    transferOptInError,
    transferLandlineSubtitle,
    transferLandlineFootnote
  } = textContent

  const brand = useBrand()

  const transferOptInVerbiage = brand === Brands.public ? transferOptInPublic : transferOptInKoodo

  const {
    phoneRef,
    alternateRef,
    accountRef,
    imeiRef,
    prevProvRef,
    alternateLandlineRef,
    firstNameRef,
    streetNameRef,
    provinceLandlineRef,
    cityLandlineRef,
    postalRef,
    tvInternetRef,
    streetNumRef
  } = transferRefs

  const mobileRefs = {
    alternateRef,
    accountRef,
    imeiRef,
    prevProvRef
  }

  const landlineRefs = {
    alternateLandlineRef,
    firstNameRef,
    streetNameRef,
    provinceLandlineRef,
    cityLandlineRef,
    postalRef,
    tvInternetRef,
    streetNumRef
  }

  const {
    handleBlurPhone,
    handlePrevProvider,
    handleIMEI,
    handleAccount,
    handleAltPhone,
    handleFirstName,
    handleLastName,
    handleAltPhoneLandline,
    handleStreetNum,
    handleStreetName,
    handleProvince,
    handleCity,
    handlePostal,
    handleTVInternet
  } = transferHandlers

  const mobileHandlers = {
    handlePrevProvider,
    handleIMEI,
    handleAccount,
    handleAltPhone
  }

  const landlineHandlers = {
    handleFirstName,
    handleLastName,
    handleAltPhoneLandline,
    handleStreetNum,
    handleStreetName,
    handleProvince,
    handleCity,
    handlePostal,
    handleTVInternet
  }

  useEffect(() => {
    if (phoneNumberState.portInPhoneNumberErroMessage) {
      handleBlurPhone()
    }
  }, [language])

  const updatePaymentStore = (payload) => {
    dispatch(updatePaymentStates(payload))
  }

  useEffect(() => {
    if (isPortInNumberEligible && isPortInAgreementChecked && !preSelectedPlan) {
      getTax(simCost, '', selectedPhoneNumber).then((responseTax) => {
        const updatedTaxProvince = getTaxProvince(responseTax)?.[0]
        updatePaymentStore({
          [PAYMENT_STATE.SUMMARY.TAX_PROVINCE]: updatedTaxProvince
        })
      })
    }
  }, [isPortInNumberEligible, isPortInAgreementChecked])

  const handleSelectNewPhoneNumber = () => {
    chooseNewPhoneNumber()
  }

  const handleSelectNewPlan = () => {
    history.push(consumerActivationPaths.Plans, {
      from: consumerActivationPaths.ChooseNumber
    })
  }

  const handleOnAgreementChange = () => {
    dispatch(
      updatePhoneNumberState({
        ...phoneNumberState,
        ...transferANumberWirelessState,
        ...transferANumberWirelineState,
        [PHONE_NUMBER_STATE.TRANSFER_A_NUMBER.ALTERNATE_CONTACT_NUMBER]: '',
        [PHONE_NUMBER_STATE.TRANSFER_A_NUMBER.ALTERNATE_CONTACT_NUMBER_ERROR]: '',
        [PHONE_NUMBER_STATE.TRANSFER_A_NUMBER.IS_PORT_IN_AGREEMENT_CHECKED]:
          !isPortInAgreementChecked,
        [PHONE_NUMBER_STATE.TRANSFER_A_NUMBER.IS_PORT_IN_HAS_AGREEMENT_ERROR]:
          isPortInAgreementChecked && hasCheckedOnce
      })
    )
    if (pushClickAnalytics) {
      pushClickAnalytics(transferOptInVerbiage, transferOptInVerbiage)
    }
  }

  return (
    <>
      <div className="w-full mt-6 space-y-4 text-black">
        <div ref={phoneRef}>
          <PhoneNumberV2 handleBlurPhoneNumber={handleBlurPhone} />
        </div>
        {isPortInNumberEligible && (
          <div>
            <div
              className={`flex flex-row text-sm text-black ${hasAgreementError ? 'mt-2' : 'my-2'}`}
            >
              <div className="mr-2">
                <Checkbox
                  testId="phoneNumber-agreement"
                  checked={isPortInAgreementChecked}
                  onChange={handleOnAgreementChange}
                  ariaLabel={brand === Brands.public ? transferOptInPublic : transferOptInKoodo}
                />
              </div>
              <Body onClick={handleOnAgreementChange} className="cursor-default">
                {brand === Brands.public ? transferOptInPublic : transferOptInKoodo}
              </Body>
            </div>
            {hasAgreementError && (
              <div className="flex items-center">
                <Image width={16} height={16} className="h-4 my-2" src={textContent.error} alt={transferOptInError} />
                <BodyError className="mt-1 ml-2 leading-4 ">{transferOptInError}</BodyError>
              </div>
            )}
          </div>
        )}
      </div>
      {isPortInNumberEligible && isPortInAgreementChecked && (
        <div className="space-y-8">
          <div className="mt-4">
            {!isPortInWireless && <Heading4>{transferLandlineSubtitle}</Heading4>}
            {!isPortInWireless && transferLandlineFootnote && <BodySmall>{transferLandlineFootnote}</BodySmall>}
          </div>
          {isPortInWireless ? (
            <TransferMobile
              mobileRefs={mobileRefs}
              mobileHandlers={mobileHandlers}
              pushClickAnalytics={pushClickAnalytics}
            />
          ) : (
            <TransferLandline
              landlineRefs={landlineRefs}
              landlineHandlers={landlineHandlers}
              pushClickAnalytics={pushClickAnalytics}
            />
          )}
        </div>
      )}
      {preSelectedPlan && (
        <PlanUnavailablePopup
          province={selectedPhoneNumber ? taxProvince : null}
          onNewPhoneNumberClick={handleSelectNewPhoneNumber}
          onNewPlanClick={handleSelectNewPlan}
        />
      )}
    </>
  )
}

export default TransferNumber
